import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, CheckCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      details: ["123 Career Street, Suite 100", "New York, NY 10001"],
    },
    {
      icon: Phone,
      title: "Call Us",
      details: ["+1 (234) 567-890", "+1 (234) 567-891"],
    },
    {
      icon: Mail,
      title: "Email Us",
      details: ["hello@wcareers.com", "support@wcareers.com"],
    },
    {
      icon: Clock,
      title: "Working Hours",
      details: ["Mon - Fri: 9AM - 6PM EST", "Sat: 10AM - 2PM EST"],
    },
  ];

  const faqs = [
    {
      question: "How long does it take to complete a course?",
      answer: "Course duration varies from 8 to 16 weeks depending on the program. Most courses require 10-15 hours per week.",
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes, we offer a 30-day money-back guarantee for all courses if you're not satisfied with your learning experience.",
    },
    {
      question: "Can I get a certificate?",
      answer: "Yes, all our courses include a certificate of completion that you can add to your LinkedIn profile.",
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-slate-50 py-16" data-testid="contact-hero">
        <div className="container-padding">
          <div className="max-w-2xl">
            <span className="overline">Contact Us</span>
            <h1 className="heading-1 mt-4">Get in Touch</h1>
            <p className="body-text text-lg mt-4">
              Have questions about our courses or career counseling? We're here to help. 
              Reach out to us and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-white" data-testid="contact-info">
        <div className="container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="p-6 border border-slate-100 rounded-xl hover:shadow-lg transition-all duration-300"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-12 h-12 bg-lime-50 rounded-xl flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-lime-600" />
                </div>
                <h3 className="font-medium text-slate-900 mb-2">{info.title}</h3>
                {info.details.map((detail, i) => (
                  <p key={i} className="text-slate-600 text-sm">{detail}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-slate-50" data-testid="contact-form-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-white p-8 rounded-2xl border border-slate-100">
              <h2 className="heading-3 mb-6">Send us a Message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-lime-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-lime-600" />
                  </div>
                  <h3 className="text-xl font-medium text-slate-900 mb-2">Message Sent!</h3>
                  <p className="text-slate-600 mb-6">We'll get back to you within 24 hours.</p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="rounded-full"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      placeholder="How can we help?"
                      data-testid="contact-subject-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      placeholder="Tell us more about your question..."
                      rows={5}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full py-6"
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? "Sending..." : "Send Message"}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              )}
            </div>

            {/* Map placeholder & FAQ */}
            <div className="space-y-8">
              <div className="bg-slate-200 rounded-2xl h-64 flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Interactive Map</p>
                  <p className="text-sm">123 Career Street, New York</p>
                </div>
              </div>

              <div>
                <h3 className="heading-3 mb-6">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white p-6 rounded-xl border border-slate-100"
                      data-testid={`faq-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-lime-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">{faq.question}</h4>
                          <p className="text-slate-600 text-sm">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
